/**
 * The printed geometry of a pass symbol, and the encoder options that produce it.
 *
 * **Here rather than in the server, because two programs render one symbol.** The A4 sheet is
 * rendered by the server, which is what keeps the four permitted values and the measured geometry
 * in one place; the browser renders the same code on screen for the person to photograph, and it
 * renders a calibration square an operator measures with a ruler to prove their printer is not
 * shrinking the page. A second copy of these numbers in the browser would be the copy that drifts,
 * and the failure is silent: a symbol a millimetre too small still looks right and decodes on a
 * clean screen.
 *
 * **Error correction `high` and a four-module quiet zone are load-bearing, and the ladder that
 * proved them is not cheap to re-run.** At `high` a twenty-one-character payload is a version-3
 * symbol, twenty-nine modules across, thirty-seven with the quiet zone. Phase 0.2 decoded 1,000 of
 * 1,000 at print size through headless Chromium and the `barcode-detector` ponyfill — at five
 * pixels per module, rotated five and ten degrees, at sixty percent contrast and with a ten percent
 * bottom-right occlusion — and found the failure boundaries at roughly twenty percent occlusion and
 * a three-pixel defocus blur. A printed pass is folded, thumbed and half-covered at a gate, which
 * is what those thirty percent of recoverable modules are for.
 *
 * **`border: 4` is the specification's own number**, not a margin choice: ISO/IEC 18004 asks for a
 * four-module quiet zone, and `border: 2` would silently violate it while still decoding on a clean
 * screen and failing on a real print. Do not lower the level and do not shrink the border without
 * re-running that ladder.
 *
 * This module holds constants alone. The encoder itself stays where each side already has it, so
 * that this package keeps no runtime dependency.
 */
/** The encoder options every rendered pass uses. Fixed here so no call site can differ. */
export const PASS_QR_OPTIONS = { ecc: 'high', border: 4 };
/**
 * The measured print geometry, as constants a layout, a decode test and a ruler all read.
 *
 * Declared rather than derived at the call site so that the printed size, the pixel size a decode
 * test rasterises at, and the millimetre size the sheet lays out at cannot drift apart: all three
 * describe one physical square.
 */
export const PASS_PRINT_GEOMETRY = Object.freeze({
    /** The symbol itself: version 3 at error-correction level `high`. */
    symbolModules: 29,
    /** The symbol plus the four-module quiet zone on each side, which is what gets printed. */
    totalModules: 37,
    /** The printed width of the symbol, excluding the quiet zone. */
    symbolMm: 25,
    /** The printed width including the quiet zone: 37 modules at 25mm/29 each. */
    totalMm: 31.9,
    /** What a printer is assumed to do, and what a decode test rasterises at. */
    dpi: 300,
    /** The whole square at that density: 37 modules times 10.18 pixels. */
    totalPx: 377,
});
//# sourceMappingURL=symbol.js.map